import { Button } from '@change/ds-core-ui/Button';
import { PetitionCard } from '@change/ds-core-ui/PetitionCard';
import { Spinner } from '@change/ds-core-ui/Spinner';
import { Text } from '@change/ds-core-ui/Text';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { RemoveOption } from './components/RemoveOption';
import { usePageBody } from './hooks/usePageBody';

export const PageBody: React.FC = () => {
	const {
		data: { petitions, hasNextPage, isLoading, isFetchingNextPage, isError },
		actions: { loadMore, trackMovementPetitionClick },
	} = usePageBody();

	const { translate, translatePlural } = useI18n();

	if (petitions.length === 0) {
		return null;
	}

	return (
		<section className="mt-10">
			<Text as="h2" variant="heading" size="small">
				<Translate value="corgi.movement.show.petition_list_heading" />
			</Text>
			<div className="mt-4 grid grid-cols-1 justify-items-center gap-x-4 gap-y-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
				{petitions.map((petition) => (
					<div key={petition.id} className="flex w-full flex-col items-center justify-center gap-2">
						<PetitionCard
							className="h-full w-full"
							imageUrl={petition.photo?.petitionMedium.url}
							petitionId={petition.id}
							location={petition.relevantLocationLocalizedName || petition.country.displayName}
							body={petition.redacted ? undefined : petition.descriptionStripped}
							isVictory={petition.status === 'VICTORY'}
							victoryLabel={translate('corgi.components.petition_card.victory')}
							petitionLinkUrl={`/p/${petition.slug}?source_location=movement_page`}
							signaturesLabel={translatePlural(
								'corgi.components.petition_card.signatures',
								petition.signatureState.signatureCount.displayed,
								{
									countLocalized: petition.signatureState.signatureCount.displayedLocalized,
								},
							)}
							title={
								petition.redacted ? translate('corgi.components.petition_card.under_review') : petition.displayTitle
							}
							petitionLinkOnClick={() => trackMovementPetitionClick(petition.id)}
						/>
						<RemoveOption petitionId={petition.id} petitionOwnerId={petition.petitionOwner.id} />
					</div>
				))}
			</div>
			<div className="mt-4 flex flex-col items-center justify-center">
				{isLoading || isFetchingNextPage ? <Spinner /> : null}

				{hasNextPage && !isFetchingNextPage && (
					<Button
						variant="secondary"
						onClick={loadMore}
						className="mt-4"
						data-qa="load-more-petitions-button"
						disabled={isLoading || isError}
					>
						<Translate value="corgi.movement.show.load_more_button" />
					</Button>
				)}
			</div>
		</section>
	);
};
